import React from "react";
import {
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  ZoomOut,
  RotateCw,
  Maximize2,
  Minimize2,
  Home,
} from "lucide-react";
import { useControls } from "react-zoom-pan-pinch";

interface MediaViewerControlsProps {
  // Navigation controls (for PDFs with pages)
  pageNumber?: number;
  numPages?: number | null;
  onPreviousPage?: () => void;
  onNextPage?: () => void;
  showPageControls?: boolean;

  // Common controls
  isFullscreen: boolean;
  isCustomFullscreen: boolean;
  onToggleFullscreen: () => void;
  onRotate: () => void;
}

const MediaViewerControls: React.FC<MediaViewerControlsProps> = ({
  pageNumber,
  numPages,
  onPreviousPage,
  onNextPage,
  showPageControls = false,
  isFullscreen,
  isCustomFullscreen,
  onToggleFullscreen,
  onRotate,
}) => {
  const { zoomIn, zoomOut, resetTransform } = useControls();

  // Use fixed positioning in any fullscreen mode, absolute otherwise
  const isAnyFullscreen = isFullscreen || isCustomFullscreen;
  const controlsPositionClass = isAnyFullscreen ? "fixed" : "absolute";

  return (
    <>
      {/* Top controls */}
      <div
        className={`${controlsPositionClass} ${
          isAnyFullscreen ? "top-4 md:top-6" : "top-2"
        } left-1/2 transform -translate-x-1/2 z-50 flex items-center gap-2 bg-white/90 backdrop-blur-sm dark:bg-black/90 rounded-lg px-3 py-2 shadow-lg`}
        style={
          isAnyFullscreen
            ? {
                top: `calc(var(--ion-safe-area-top, 0px) + 1rem)`,
              }
            : undefined
        }
      >
        <button
          onClick={() => zoomOut()}
          className="flex items-center justify-center min-w-[44px] min-h-[44px] md:w-10 md:h-10 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors duration-200 touch-manipulation active:bg-gray-300 dark:active:bg-gray-600"
          aria-label="Zoom out"
        >
          <ZoomOut className="w-5 h-5" />
        </button>

        <button
          onClick={() => resetTransform()}
          className="flex items-center justify-center min-w-[44px] min-h-[44px] md:w-10 md:h-10 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors duration-200 touch-manipulation active:bg-gray-300 dark:active:bg-gray-600"
          aria-label="Reset zoom"
        >
          <Home className="w-5 h-5" />
        </button>

        <button
          onClick={() => zoomIn()}
          className="flex items-center justify-center min-w-[44px] min-h-[44px] md:w-10 md:h-10 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors duration-200 touch-manipulation active:bg-gray-300 dark:active:bg-gray-600"
          aria-label="Zoom in"
        >
          <ZoomIn className="w-5 h-5" />
        </button>

        <div className="w-px h-6 bg-gray-300 dark:bg-gray-600" />

        <button
          onClick={onRotate}
          className="flex items-center justify-center min-w-[44px] min-h-[44px] md:w-10 md:h-10 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors duration-200 touch-manipulation active:bg-gray-300 dark:active:bg-gray-600"
          aria-label="Rotate"
        >
          <RotateCw className="w-5 h-5" />
        </button>

        <button
          onClick={onToggleFullscreen}
          className="flex items-center justify-center min-w-[44px] min-h-[44px] md:w-10 md:h-10 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors duration-200 touch-manipulation active:bg-gray-300 dark:active:bg-gray-600"
          aria-label={isAnyFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
        >
          {isAnyFullscreen ? (
            <Minimize2 className="w-5 h-5" />
          ) : (
            <Maximize2 className="w-5 h-5" />
          )}
        </button>
      </div>

      {/* Bottom page controls - only show for PDFs */}
      {showPageControls &&
        pageNumber !== undefined &&
        onPreviousPage &&
        onNextPage && (
          <div
            className={`${controlsPositionClass} ${
              isAnyFullscreen ? "bottom-4 md:bottom-6" : "bottom-2"
            } left-1/2 transform -translate-x-1/2 z-50 flex items-center gap-3 bg-white/90 backdrop-blur-sm dark:bg-black/90 rounded-lg px-4 py-2 shadow-lg`}
            style={
              isAnyFullscreen
                ? {
                    bottom: `calc(var(--ion-safe-area-bottom, 0px) + 1rem)`,
                  }
                : undefined
            }
          >
            <button
              onClick={onPreviousPage}
              disabled={pageNumber <= 1}
              className="flex items-center justify-center min-w-[44px] min-h-[44px] md:w-10 md:h-10 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors duration-200 touch-manipulation active:bg-gray-300 dark:active:bg-gray-600"
              aria-label="Previous page"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>

            <div className="flex items-center gap-2 px-2 text-sm font-medium text-gray-800 dark:text-gray-200 min-w-0">
              <span className="font-mono">{pageNumber}</span>
              <span className="text-gray-500 dark:text-gray-400">/</span>
              <span className="text-gray-500 dark:text-gray-400 font-mono">
                {numPages || "--"}
              </span>
            </div>

            <button
              onClick={onNextPage}
              disabled={pageNumber >= (numPages || 0)}
              className="flex items-center justify-center min-w-[44px] min-h-[44px] md:w-10 md:h-10 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors duration-200 touch-manipulation active:bg-gray-300 dark:active:bg-gray-600"
              aria-label="Next page"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        )}
    </>
  );
};

export default MediaViewerControls;
